import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";

export default function TestimonialsSection() {
  const { t } = useLang();
  const { content } = useContent();
  const items = content?.testimonials || [];
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [idx, setIdx] = useState(0);

  const onSelect = useCallback(() => embla && setIdx(embla.selectedScrollSnap()), [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    const id = setInterval(() => embla.scrollNext(), 5500);
    return () => clearInterval(id);
  }, [embla, onSelect]);

  return (
    <section className="section-pad bg-[var(--surface)] relative" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="overline mb-3">{t("testimonials.overline")}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
            {t("testimonials.title")}
          </h2>
        </div>

        <div className="embla" ref={emblaRef}>
          <div className="embla__container -ml-4">
            {items.map((it) => (
              <div
                key={`${it.name}-${it.role}`}
                className="pl-4 shrink-0"
                style={{ flex: "0 0 100%", maxWidth: "100%" }}
              >
                <div className="md:mx-12 lg:mx-24">
                  <div className="card-dark p-8 sm:p-12 text-center">
                    <Quote className="text-[var(--gold)]/40 mx-auto mb-6" size={40} />
                    <p className="font-display text-xl sm:text-2xl lg:text-3xl text-white/95 leading-relaxed mb-8 max-w-3xl mx-auto italic">
                      "{it.text}"
                    </p>
                    <div className="flex justify-center gap-1 mb-6">
                      {Array.from({ length: it.rating || 5 }).map((_, k) => (
                        <Star key={`${it.name}-star-${k}`} size={16} className="star fill-[var(--gold)]" />
                      ))}
                    </div>
                    <div className="flex items-center justify-center gap-3">
                      <img src={it.avatar} alt={it.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-[var(--gold)]/40" />
                      <div className="text-left">
                        <div className="font-semibold text-white">{it.name}</div>
                        <div className="text-xs text-white/55">{it.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center items-center gap-4 mt-8">
          <button
            onClick={() => embla && embla.scrollPrev()}
            className="w-11 h-11 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition"
            aria-label="Prev testimonial"
            data-testid="testimonial-prev"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex gap-1.5">
            {items.map((it, i) => (
              <button
                key={`${it.name}-dot`}
                onClick={() => embla && embla.scrollTo(i)}
                className={`h-1 rounded-full transition-all ${i === idx ? "w-8 bg-[var(--gold)]" : "w-2 bg-white/20"}`}
                aria-label={`Go to ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={() => embla && embla.scrollNext()}
            className="w-11 h-11 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition"
            aria-label="Next testimonial"
            data-testid="testimonial-next"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
}
